import { Button, Text } from "@chakra-ui/react";
import React, { useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { connect, useSelector } from "react-redux";
import { useNavigate } from "react-router";

import { RanaModal } from "@nens/ranalib";

import { APP_BASE_HREF, AppDispatch } from "../App";
import { backToStartScreen } from "../state/appState";
import { ActiveModal, inactivateModal, setActiveModal } from "../state/mainGui";
import {
  getSelectedOrganisation,
  getSimulationStatus,
  getThreediModel,
  shutdownSimulation,
} from "../state/simulation";

interface QuitSimulationProps extends Props {
  restart?: boolean;
}

const QuitSimulation: React.FC<QuitSimulationProps> = (props) => {
  const { restart = false } = props;
  const { shutdownSimulation, backToStartScreen } = props;

  const organisation = useSelector(getSelectedOrganisation);
  const navigate = useNavigate();
  const threedimodel = useSelector(getThreediModel);
  const intl = useIntl();
  const simulationTime = useSelector(getSimulationStatus).time;

  const quitOrRestart = useCallback(() => {
    shutdownSimulation();
    backToStartScreen();

    if (restart && organisation && threedimodel) {
      navigate(
        `${APP_BASE_HREF}/quickstart/model-and-organisation/${threedimodel.id}/${organisation.uniqueId}/`
      );
    }
  }, [shutdownSimulation, backToStartScreen, restart, organisation, threedimodel, navigate]);

  return (
    <RanaModal
      isOpen={true}
      topBar={
        restart ? (
          <FormattedMessage
            id="quit_modal.title_restart_simulation"
            defaultMessage="Restart simulation?"
          />
        ) : (
          <FormattedMessage
            id="quit_modal.title_quit_simulation"
            defaultMessage="Quit simulation?"
          />
        )
      }
      onClose={props.inactivateModal}
      buttons={[
        <Button variant="tertiary" onClick={props.inactivateModal}>
          <FormattedMessage id="quit_modal.cancel_button" defaultMessage="Cancel" />
        </Button>,
        <Button variant="secondary" onClick={quitOrRestart}>
          {restart ? (
            <FormattedMessage
              id="quit_modal.restart_not_store_button"
              defaultMessage="Restart, don't store results"
            />
          ) : (
            <FormattedMessage
              id="quit_modal.quit_not_store_button"
              defaultMessage="Quit, don't store results"
            />
          )}
        </Button>,
        <Button
          variant="secondary"
          onClick={() => {
            props.setActiveModal("SAVE_RESULTS");
          }}
          title={
            simulationTime === 0
              ? intl.formatMessage({
                  id: "quit_modal.tooltip_quit_store_button_disabled",
                  defaultMessage: "Simulation has not started yet",
                })
              : ""
          }
          isDisabled={simulationTime === 0}
        >
          {restart ? (
            <FormattedMessage
              id="quit_modal.restart_store_button"
              defaultMessage="Store results instead of restarting"
            />
          ) : (
            <FormattedMessage
              id="quit_modal.quit_store_button"
              defaultMessage="Quit, store results"
            />
          )}
        </Button>,
      ]}
    >
      <Text mb="8px">
        <FormattedMessage
          id="quit_modal.content_text"
          defaultMessage="The simulation will stop running. You have the option of storing your results."
        />
      </Text>
      <Text fontWeight="bold" color="brand.analytic">
        {restart ? (
          <FormattedMessage
            id="quit_modal.confirmation_text_restart"
            defaultMessage="Do you want to restart the simulation?"
          />
        ) : (
          <FormattedMessage
            id="quit_modal.confirmation_text_stop"
            defaultMessage="Do you want to stop the simulation?"
          />
        )}
      </Text>
    </RanaModal>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    inactivateModal: () => dispatch(inactivateModal()),
    setActiveModal: (activeModal: ActiveModal) => dispatch(setActiveModal(activeModal)),
    shutdownSimulation: () => dispatch(shutdownSimulation()),
    backToStartScreen: () => dispatch(backToStartScreen()),
  };
};
type Props = ReturnType<typeof mapDispatchToProps>;

export default connect(null, mapDispatchToProps)(QuitSimulation);
